"use client"

import React from "react";
import Link from "next/link";
import { ArrowRight, Bug, Check, ChevronDown, ClipboardPaste, Code2, FileSearch, Globe, Link2, Mail, Network, Radar, ShieldAlert, ShieldCheck, Wrench } from "lucide-react";
import type { SiteTranslations } from "@/lib/translations/types";
import { useTranslations } from "@/components/i18n/use-translations";
import { buildLocalizedPath } from "@/lib/i18n";

type ScanShowcaseProps = {
    showcase: SiteTranslations["home"]["showcase"];
    onCta: () => void;
};

const FLAGGED_FEEDS = ["OpenPhish", "Phishing Database", "ThreatFox"];
const SUPPORTED_TYPES = ["URL", "Domain", "IP", "Email", "MD5", "SHA1", "SHA256", "File"];
const STEP_ICONS = [ClipboardPaste, Radar, ShieldCheck];
const CHECK_ICONS = [Link2, Globe, Network, Mail, FileSearch];
// Mirrors the real result card's "Alternative Indicators" rows (documentation-range values)
const EXAMPLE_PIVOTS = [
    { label: "IPv4 · A Record", values: [{ value: "203.0.113.24", tagged: true }, { value: "203.0.113.25", tagged: false }] },
    { label: "Domain · MX Record", values: [{ value: "mx.wallet-verify-login.example", tagged: false }] },
];
const SEPARATOR = "h-px border-0 bg-linear-to-r from-transparent via-cyan-700/50 to-transparent";

/**
 * Idle-state explainer: an illustrated (mock) scan result plus a 3-step "how it works".
 * Example indicators use reserved documentation names (RFC 2606 / RFC 5737), not real sites.
 */
export default function ScanShowcase({ showcase, onCta }: ScanShowcaseProps) {
    const { lang, messages } = useTranslations();
    const { checks } = messages.home;

    return (
        <section aria-labelledby="showcase-title" className="w-full flex flex-col gap-8 pt-2 motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-4 motion-safe:duration-700">
            {/* Divider between the scan box and the explainer */}
            <div className="flex items-center gap-4" aria-hidden="true">
                <span className="h-px flex-1 bg-linear-to-r from-transparent via-cyan-500/25 to-cyan-500/50" />
                <span className="grid h-8 w-8 place-items-center rounded-full border border-cyan-500/30 bg-slate-900/80 shadow-[0_0_18px_-4px_rgba(34,211,238,0.55)]">
                    <ChevronDown className="h-4 w-4 text-cyan-400 motion-safe:animate-bounce" />
                </span>
                <span className="h-px flex-1 bg-linear-to-l from-transparent via-cyan-500/25 to-cyan-500/50" />
            </div>

            <div className="text-center flex flex-col items-center gap-2">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-cyan-400">{showcase.eyebrow}</p>
                <h2 id="showcase-title" className="text-2xl sm:text-3xl font-semibold text-slate-100">{showcase.title}</h2>
                <p className="text-sm text-gray-400 max-w-xl leading-relaxed">{showcase.description}</p>
            </div>

            <div className="grid grid-cols-[minmax(0,1fr)] gap-8 sm:grid-cols-2 sm:items-center">
                {/* Mock result window */}
                <div className="relative pb-6 sm:pb-8" aria-hidden="true">
                    <div className="absolute -inset-6 bg-[radial-gradient(ellipse_at_center,rgba(34,211,238,0.15),transparent_70%)] blur-2xl pointer-events-none" />
                    <div className="relative overflow-hidden rounded-2xl border border-cyan-700/40 bg-linear-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xs shadow-[0_0_32px_-8px_rgba(34,211,238,0.35)]">
                        <div className="flex items-center gap-1.5 border-b border-slate-700/60 px-4 py-2.5">
                            <span className="h-2.5 w-2.5 rounded-full bg-red-500/70" />
                            <span className="h-2.5 w-2.5 rounded-full bg-amber-400/70" />
                            <span className="h-2.5 w-2.5 rounded-full bg-green-500/70" />
                            <span className="ms-3 text-[0.65rem] uppercase tracking-[0.14em] text-slate-500">{showcase.exampleLabel}</span>
                        </div>

                        {/* Same order as the real result: verdict, detections and sources, then pivots */}
                        <div className="relative flex flex-col gap-3.5 p-4">
                            <div className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-linear-to-b from-transparent via-cyan-400/10 to-transparent motion-safe:animate-scan-sweep" />

                            <div className="flex items-start justify-between gap-3">
                                <div className="flex min-w-0 flex-col gap-2">
                                    <p className="text-[0.6rem] uppercase tracking-[0.12em] text-slate-400">{messages.home.indicatorLabel}</p>
                                    <div className="flex min-w-0 items-center gap-1.5">
                                        <span className="shrink-0 rounded-full border border-cyan-500/50 bg-cyan-500/10 px-1.5 py-px text-[0.55rem] uppercase tracking-[0.1em] text-gray-300">Domain</span>
                                        <span dir="ltr" className="truncate font-mono text-xs font-semibold text-red-500">wallet-verify-login.example</span>
                                    </div>
                                </div>
                                <div className="flex shrink-0 flex-col items-end gap-2">
                                    <p className="text-[0.6rem] uppercase tracking-[0.12em] text-slate-400">{messages.home.scanResultLabel}</p>
                                    <span className="inline-flex items-center gap-1 rounded-full border border-red-600/50 bg-red-700 px-2.5 py-0.5 text-xs font-semibold text-white shadow-lg shadow-red-600/30">
                                        <ShieldAlert className="h-3.5 w-3.5" />
                                        Phishing
                                    </span>
                                </div>
                            </div>

                            <hr className={SEPARATOR} />

                            <div className="flex flex-col gap-2">
                                <p className="flex items-center justify-between text-[0.6rem] uppercase tracking-[0.12em] text-slate-400">
                                    {messages.home.detectionsLabel}
                                    <span className="rounded-full border border-red-600/50 bg-red-600/10 px-1.5 text-[0.6rem] text-red-500">{FLAGGED_FEEDS.length}</span>
                                </p>
                                <ul className="flex flex-wrap gap-1.5">
                                    {FLAGGED_FEEDS.map((feed) => (
                                        <li key={feed} className="inline-flex items-center gap-1.5 rounded-full border border-red-600/50 bg-red-600/10 px-2 py-0.5 text-[0.65rem] font-medium text-red-400">
                                            <Bug className="h-3 w-3" />
                                            {feed}
                                        </li>
                                    ))}
                                    <li className="inline-flex items-center gap-1.5 rounded-full border border-green-600/50 bg-green-600/10 px-2 py-0.5 text-[0.65rem] font-medium text-green-400">
                                        <Check className="h-3 w-3" />
                                        {showcase.moreFeeds}
                                    </li>
                                </ul>
                            </div>

                            <hr className={SEPARATOR} />

                            <div className="flex flex-col gap-2">
                                <p className="text-[0.6rem] uppercase tracking-[0.12em] text-slate-400">{messages.home.pivotsLabel}</p>
                                <dl className="grid grid-cols-[112px_minmax(0,1fr)] items-start gap-x-2.5 gap-y-1.5">
                                    {EXAMPLE_PIVOTS.map((pivot) => (
                                        <React.Fragment key={pivot.label}>
                                            <dt className="pt-0.5 text-[0.55rem] uppercase tracking-widest text-gray-500">{pivot.label}</dt>
                                            <dd className="flex min-w-0 flex-wrap gap-1">
                                                {pivot.values.map((item) => (
                                                    <span
                                                        key={item.value}
                                                        dir="ltr"
                                                        className={`max-w-full truncate rounded border px-1.5 py-px font-mono text-[0.65rem] ${
                                                            item.tagged ? "border-red-600/50 bg-red-600/10 font-semibold text-red-500" : "border-slate-700/70 bg-slate-950/50 text-gray-300"
                                                        }`}
                                                    >
                                                        {item.value}
                                                    </span>
                                                ))}
                                            </dd>
                                        </React.Fragment>
                                    ))}
                                </dl>
                            </div>
                        </div>
                    </div>

                    {/* Floating "phishing" result */}
                    <div className="absolute -bottom-1 inset-e-4 sm:inset-e-6 flex items-center gap-2 rounded-xl border border-red-600/40 bg-slate-900/90 px-3 py-2 shadow-lg shadow-red-600/10 backdrop-blur-xs motion-safe:animate-float-slow">
                        <ShieldAlert className="h-4 w-4 text-red-400" />
                        <span dir="ltr" className="font-mono text-xs text-slate-200">wallet-verify-login.example</span>
                        <span className="rounded-full bg-red-700 px-2 py-0.5 text-[0.65rem] font-semibold text-white">Phishing</span>
                    </div>
                </div>

                {/* How it works */}
                <ol className="flex flex-col gap-5">
                    {showcase.steps.map((step, index) => {
                        const StepIcon = STEP_ICONS[index];
                        return (
                            <li key={step.title} className="flex gap-4">
                                <span className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-cyan-700/40 bg-cyan-500/10 text-cyan-300">
                                    <StepIcon className="h-5 w-5" />
                                    <span className="absolute -top-1.5 -inset-e-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-cyan-500 text-[0.6rem] font-bold text-slate-950">{index + 1}</span>
                                </span>
                                <div className="flex flex-col gap-1">
                                    <p className="text-sm font-semibold text-slate-100">{step.title}</p>
                                    <p className="text-xs leading-relaxed text-gray-400">{step.body}</p>
                                </div>
                            </li>
                        );
                    })}
                    <li>
                        <button
                            type="button"
                            onClick={onCta}
                            className="inline-flex items-center gap-2 rounded-full border border-cyan-500/50 bg-cyan-500/10 px-4 py-2 text-sm font-medium text-cyan-300 transition-colors hover:bg-cyan-500/20 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-cyan-400"
                        >
                            {showcase.cta}
                            <ArrowRight className="h-4 w-4 rtl:rotate-180" />
                        </button>
                    </li>
                </ol>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-2">
                <span className="text-xs text-slate-500">{showcase.supportedLabel}</span>
                {SUPPORTED_TYPES.map((type) => (
                    <span key={type} className="rounded-md border border-slate-700/60 bg-slate-800/40 px-2 py-0.5 text-[0.7rem] font-medium uppercase tracking-[0.08em] text-slate-300">
                        {type}
                    </span>
                ))}
            </div>

            <hr className={SEPARATOR} />

            {/* What you can check: crawlable, keyword-rich description of every lookup type, plus internal links */}
            <section aria-labelledby="checks-title" className="flex flex-col gap-6">
                <div className="text-center flex flex-col items-center gap-2">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-cyan-400">{checks.eyebrow}</p>
                    <h2 id="checks-title" className="text-2xl sm:text-3xl font-semibold text-slate-100">{checks.title}</h2>
                    <p className="text-sm text-gray-400 max-w-xl leading-relaxed">{checks.description}</p>
                </div>
                <ul className="grid grid-cols-[minmax(0,1fr)] gap-3 sm:grid-cols-2">
                    {checks.items.map((item, index) => {
                        const CheckIcon = CHECK_ICONS[index];
                        return (
                            <li key={item.title} className="flex gap-3 rounded-xl border border-slate-800/80 bg-slate-900/50 p-4 transition-colors hover:border-cyan-700/50">
                                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-cyan-700/40 bg-cyan-500/10 text-cyan-300">
                                    <CheckIcon className="h-4.5 w-4.5" aria-hidden="true" />
                                </span>
                                <div className="flex flex-col gap-1">
                                    <h3 className="text-sm font-semibold text-slate-100">{item.title}</h3>
                                    <p className="text-xs leading-relaxed text-gray-400">{item.body}</p>
                                </div>
                            </li>
                        );
                    })}
                    <li className="flex flex-col justify-center gap-2 rounded-xl border border-cyan-700/40 bg-linear-to-br from-cyan-500/10 to-blue-600/10 p-4">
                        <p className="text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-cyan-300">{checks.moreLabel}</p>
                        <Link href={buildLocalizedPath("/tools", lang)} className="group inline-flex items-center gap-2 text-sm font-medium text-slate-100 transition-colors hover:text-cyan-200">
                            <Wrench className="h-4 w-4 text-cyan-300" aria-hidden="true" />
                            {checks.toolsLink}
                            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 rtl:rotate-180" aria-hidden="true" />
                        </Link>
                        <Link href={buildLocalizedPath("/developers", lang)} className="group inline-flex items-center gap-2 text-sm font-medium text-slate-100 transition-colors hover:text-cyan-200">
                            <Code2 className="h-4 w-4 text-cyan-300" aria-hidden="true" />
                            {checks.apiLink}
                            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 rtl:rotate-180" aria-hidden="true" />
                        </Link>
                    </li>
                </ul>
            </section>
        </section>
    );
}
